// Approval card (prototype .ap): what an employee wants to change, waiting for the boss.
// Approve runs it exactly as shown. Change turns the editable rows into inputs ("Save and approve" runs it with
// the edited values). Reject asks why — the reason goes back to the employee so they can fix it.
//
// The card spec comes from the server (aiOffice/cards.js): { kind, title, rows: [{ label, value, edit? }], table?, flags? }
import { useState } from 'react';
import Avatar from '../Avatar';
import { when, day } from '../fmt';

const REASONS = ['Wrong amount', 'Wrong quantity', 'Wrong customer', 'Not now'];
const DONE_LABEL = { approved: 'Approved', done: 'Done', rejected: 'Rejected', failed: 'Failed' };

// Dates: shown as "8 Oct 2026", edited with a date picker. A date may come as '2026-10-08' or '20261008'.
const isoDate = (v) => { const m = /^(\d{4})-?(\d{2})-?(\d{2})$/.exec(String(v ?? '').trim()); return m ? `${m[1]}-${m[2]}-${m[3]}` : null; };
const isDateRow = (r) => !!r.edit && (r.edit.type === 'date' || ((!r.edit.type || r.edit.type === 'text') && /date|due|delivery/i.test(r.label) && !!isoDate(r.edit.value)));
const startValue = (r) => (isDateRow(r) && isoDate(r.edit.value)) || r.edit.value;
const shown = (r) => day(r.value, isDateRow(r));

function Field({ edit, label, value, date, autoFocus, onChange }) {
  const common = { 'aria-label': label, value, autoFocus, onChange: (e) => onChange(e.target.value) };
  if (edit.type === 'select') return <select {...common}>{(edit.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  if (edit.type === 'textarea') return <textarea rows={3} {...common} />;
  if (date) return <input type="date" {...common} />;
  return <input type="text" inputMode={edit.type === 'number' ? 'decimal' : undefined} {...common} />;
}

export default function ApprovalCard({ item, agent, inChat, onDecide }) {
  const card = item.card || { kind: item.tool, title: item.tool, rows: [] };
  const rows = card.rows || [];
  const editable = rows.filter((r) => r.edit);
  const firstEdit = rows.findIndex((r) => r.edit);
  const [mode, setMode] = useState('idle'); // idle | edit | reject | busy
  const [values, setValues] = useState({});
  const [other, setOther] = useState('');
  const [error, setError] = useState(null);
  const pending = item.status === 'pending';

  const decide = async (decision) => {
    if (!onDecide) return;
    setMode('busy'); setError(null);
    try {
      await onDecide(item, decision);
    } catch (err) {
      setError(err.message || 'Something went wrong. Nothing was run.');
      setMode(decision.choice === 'reject' ? 'reject' : decision.edited ? 'edit' : 'idle');
    }
  };

  const startEdit = () => {
    setValues(Object.fromEntries(editable.map((r) => [r.edit.path, startValue(r)])));
    setMode('edit');
  };
  const saveAndApprove = () => {
    const edited = {};
    for (const r of editable) if (values[r.edit.path] !== startValue(r)) edited[r.edit.path] = values[r.edit.path];
    decide({ choice: 'approve', edited });
  };

  const cls = pending ? '' : item.status === 'rejected' || item.status === 'failed' ? 'done-no' : 'done-ok';
  return (
    <div className={`ap ${cls}`}>
      <div className="ap-h">
        {!inChat && <div className="who"><Avatar agent={agent} size="sm" />{agent ? agent.name : item.agentId} · {when(item.createdAt)}</div>}
        <span className="kind">{pending ? `Needs your OK · ${card.kind}` : `${DONE_LABEL[item.status] || item.status} · ${card.kind}`}</span>
        <b>{card.title}</b>
      </div>
      <div className="ap-b">
        {card.table && card.table.rows && card.table.rows.length > 0 && (
          <div className="tblw"><table>
            <thead><tr>{card.table.head.map((h, i) => <th key={i} className={(card.table.numCols || []).includes(i) ? 'num' : ''}>{h}</th>)}</tr></thead>
            <tbody>{card.table.rows.map((r, j) => <tr key={j}>{r.map((c, i) => <td key={i} className={(card.table.numCols || []).includes(i) ? 'num' : ''}>{c}</td>)}</tr>)}</tbody>
          </table></div>
        )}
        <dl className="kv">
          {rows.map((r, i) => [
            <dt key={`k${i}`}>{r.label}</dt>,
            <dd key={`v${i}`}>
              {mode === 'edit' && r.edit
                ? <Field edit={r.edit} label={r.label} value={values[r.edit.path] ?? ''} autoFocus={i === firstEdit}
                  date={isDateRow(r) && (!r.edit.value || !!isoDate(r.edit.value))} onChange={(v) => setValues((s) => ({ ...s, [r.edit.path]: v }))} />
                : shown(r)}
            </dd>,
          ])}
        </dl>
        {card.flags && card.flags.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {card.flags.map(([t, tone], i) => <span key={i} className={`flag ${tone || ''}`}>{t}</span>)}
          </div>
        )}
        {pending && <div className="note">Nothing is saved until you approve.</div>}
        {error && <div className="ap-err">{error}</div>}
      </div>
      <div className="ap-a">
        {!pending && (
          <span className="res">
            {item.status === 'rejected' && `You rejected this: ${item.reason || ''}`}
            {(item.status === 'done' || item.status === 'approved') && `✓ You approved this${item.decidedAt ? ` at ${when(item.decidedAt)}` : ''}${item.changes && item.changes.length ? ' (with your change)' : ''}${item.status === 'approved' ? ' · carrying it out…' : ''}`}
            {item.status === 'failed' && `✕ Approved, but it did not go through: ${(item.result || '').split('\n')[0].slice(0, 160)}`}
          </span>
        )}
        {pending && !onDecide && <span className="res">Waiting for your OK</span>}
        {pending && onDecide && mode === 'idle' && <>
          <button className="aio-btn ok" onClick={() => decide({ choice: 'approve' })}>Approve</button>
          {editable.length > 0 && <button className="aio-btn" onClick={startEdit}>Change</button>}
          <button className="aio-btn ghost danger" onClick={() => setMode('reject')}>Reject</button>
        </>}
        {pending && mode === 'edit' && <>
          <button className="aio-btn ok" onClick={saveAndApprove}>Save and approve</button>
          <button className="aio-btn ghost" onClick={() => { setMode('idle'); setError(null); }}>Cancel</button>
        </>}
        {pending && mode === 'reject' && (
          <div className="reasons">
            <p>Why? This helps them learn.</p>
            {REASONS.map((r) => <button key={r} className="aio-btn" onClick={() => decide({ choice: 'reject', reason: r })}>{r}</button>)}
            <form className="reason-other" onSubmit={(e) => { e.preventDefault(); if (other.trim()) decide({ choice: 'reject', reason: other.trim() }); }}>
              <input value={other} onChange={(e) => setOther(e.target.value)} placeholder="Or type the reason" aria-label="Reason" />
              <button className="aio-btn" type="submit" disabled={!other.trim()}>Send</button>
              <button className="aio-btn ghost" type="button" onClick={() => setMode('idle')}>Cancel</button>
            </form>
          </div>
        )}
        {pending && mode === 'busy' && <span className="res">Working…</span>}
      </div>
    </div>
  );
}
